import { Button, Col, Row, Form, Label} from 'reactstrap';
import '../ReactSearchSelect.css';
import "../GlobalStyles.css";
import { useEffect, useMemo, useState } from 'react';
import DriverService from "../../Services/DriverService";
import Driver from "../../Models/Driver";
import ChargeResponse from '../../Models/Responses/ChargeResponse';
import { toast } from 'react-toastify';
import ChargeRate from '../../Models/ChargeRate';
import ChargePayoutResponse from '../../Models/Responses/ChargePayoutResponse';
import { useMsal } from "@azure/msal-react";

interface Props{
    driver?: Driver;
    props: any;
    employeeContactId: string;
    getCompanyCarData: Function;
    setIsLoading: Function;
    isLoading: boolean;
    chargeRates: ChargeRate[];
}

const UserChargesRegistrationForm = (props:Props) => {
    
    const {instance} = useMsal();
    const driverService = useMemo(() => new DriverService(instance), [instance]);

    const [formData, setFormData] = useState({
        companyCar: props.driver?.companyCar.licensePlate,
        date: new Date().toISOString().split('T')[0],
        quantity: 0,
        chargeCost: 0,
        unit: "percentage"

    })
    const [percentage, setPercentage] = useState<Number>(0)
    const [chargeCost, setChargeCost] = useState<Number>(0)
    const [payoutId, setPayoutId] = useState<string>('')
    const [firstAvailableDate, setFirstAvailableDate] = useState<Date>();
    
    async function getFirstAvailableDate(){
        try {
            const chargePayouts = await driverService.GetAllChargePayoutsByEmployeeContactId(props.employeeContactId);
            
            const mostRecentPayoutDate = new Date(chargePayouts.filter((payout) => payout.payoutStatus === 1)
                                            .sort((a, b) => new Date(b.payoutDate).getTime() - new Date(a.payoutDate).getTime())[0].payoutDate);
            setFirstAvailableDate(mostRecentPayoutDate);
        } catch (error) {
            console.log(error) //Error logs, but everything keeps working as expected, added a try catch as a temporary solution - will come back to this later
        }
    }
   
    async function handleSubmit(event:any){
        event.preventDefault();

        if(validateUserInput() === "SUCCESS"){
            saveCharge();
        }
        else{
            toast.error(validateUserInput())
        }
     }

     function validateUserInput(): string {
        if (isNaN(Number(formData.quantity))) {
            return "Hoeveelheid moet een geldig getal zijn.";
        }
        if (new Date(formData.date) > new Date()) {
            return "Datum mag niet in de toekomst zijn.";
        }
        if (formData.quantity <= 0) {
            return "Hoeveelheid moet boven 0 zijn.";
        }
        if (formData.unit === 'kW' && formData.quantity > (props.driver?.companyCar?.batteryCapacity ?? 0)) {
            return "Het aantal kW kan niet groter zijn dan uw batterijcapaciteit.";
        }
        if (firstAvailableDate && new Date(formData.date) < new Date(firstAvailableDate.setHours(0, 0, 0, 0))) {
            return "U kan geen laadbeurten registreren vóór uw meest recente uitbetalingsdatum.";
        }
        return "SUCCESS";
    }
    
    

    function handleChange(event: any){
        const {id, value} = event.target;
        setFormData(prevFormData =>{
            return {
                ...prevFormData,
                [id]: value
            }
        })
    }

    function getCurrentChargeRate(rates : ChargeRate[]){
        const currentRate = rates.filter((rate) => rate.startDate <= new Date(formData.date) && rate.endDate >= new Date(formData.date) )
        return currentRate[0];
    }

    async function getActivePayoutRecord(){
        
        //Checks for active payout - no payout active? The API call will automatically add a new active record and otherwise it does nothing.
        try {
            const payoutResponse: ChargePayoutResponse = {
                companyCarId: props.driver?.companyCar?.companyCarId || "",
                driverId: props.driver?.driverId || "",
                payoutStatus: 0,
                payoutDate: new Date()
            }
                driverService.AddChargePayout(payoutResponse).then(async (isAdded) => {
                    if (isAdded){
                        //
                        let activeRecord = (await driverService.GetAllChargePayoutsByEmployeeContactId(props.employeeContactId)).filter((payout) => payout.payoutStatus === 0);
                        setPayoutId(activeRecord[0].chargePayoutId);

                    }
                    else{
                        toast.error("Er is iets fout gegaan tijdens het registreren van de laadbeurt.")
                    }
                }).finally(() => {
                    props.setIsLoading(false);
                })
        } catch (error) {
            console.log(error)
        }

        
    }
    useEffect(() => {
        props.setIsLoading(true)
        getFirstAvailableDate();
        getActivePayoutRecord();
    }, []);
    
    async function saveCharge(){
        try {
            const chargeToSave: ChargeResponse = {
                companyCarId: props.driver?.companyCar?.companyCarId || "",
                chargeDate: new Date(formData.date),
                chargePercentage: percentage as number,
                chargeCost: parseFloat(chargeCost.toFixed(2)) as number,
                chargeRateId: getCurrentChargeRate(props.chargeRates).chargeRateId,
                stateCode: 0,
                chargePayoutId: payoutId
            }
            
            driverService?.AddCharge(chargeToSave).then((isAdded) => {
                if (isAdded){
                    toast.success("Laadbeurt is succesvol toegevoegd.")

                    setFormData({
                        companyCar: props.driver?.companyCar.licensePlate,
                        date: new Date().toISOString().split('T')[0],
                        quantity: 0,
                        chargeCost: 0,
                        unit: formData.unit
                    });

                }
                else{
                    toast.error("Er is iets fout gegaan tijdens het registreren van de laadbeurt.")
                }
            }).finally(() =>{

            })
        } catch (error) {
            toast.error("Er is geen laadtarief gebonden aan deze datum.")
        }
    }

    useEffect(() =>{
        try {
            if(props.chargeRates.length !== 0){
                calculateChargeCost(formData.quantity)
            }
        } catch (error) {
            console.log(error)
        }
       
    },[formData.quantity, formData.date, formData.unit])                                                                     

    function calculateChargeCost(quantity: number) {
        const currentRate = getCurrentChargeRate(props.chargeRates).rate;
        const batteryCapacity = props.driver?.companyCar?.batteryCapacity;
    
        let percentage = 0;
        let chargeCost = 0;
    
        if (formData.unit === 'kW') {
            percentage = batteryCapacity ? (quantity / batteryCapacity) * 100 : 0;
            chargeCost = batteryCapacity ? (batteryCapacity * (percentage / 100) * currentRate) : 0;
        } else {
            percentage = quantity;
            chargeCost = batteryCapacity ? (batteryCapacity * (percentage / 100) * currentRate) : 0;
        }
    
        setPercentage(percentage);
        setChargeCost(chargeCost);
    }

    function getChargeRegistrationForm(){
        return (
            <div className="p-0 m-0 mt-3">
            <Form onSubmit={handleSubmit}>
                <h5 className="mb-3"><b>Registratie</b></h5>
                <Row className="pl-2 pr-2 mb-2 d-flex align-items-center">
                    <Col xs="12" className="mb-1">
                        <Label className="m-0">Eenheid:</Label>
                    </Col>
                    <Col xs="12">
                        <select id="unit" onChange={handleChange} className="inputs-fixed-height form-control">
                            <option value="percentage">Percentage</option>
                            <option value="kW">kW</option>
                        </select>
                    </Col>
                </Row>
                <Row className="pl-2 pr-2 mb-2 d-flex align-items-center">
                    <Col xs="12" className="mb-1">
                        <Label className="m-0">Datum:</Label>
                    </Col>
                    <Col xs="12">
                        <input id="date" value={formData.date} type="date"className="inputs-fixed-height form-control" onChange={handleChange}/>
                    </Col>
                </Row>
                <Row className="pl-2 pr-2 mb-2 d-flex align-items-center">
                    <Col xs="12" className="mb-1">
                        <Label className="m-0">Hoeveelheid:</Label>
                    </Col>
                    <Col xs="12">
                        <input id="quantity" value={formData.quantity} type="text"className="inputs-fixed-height form-control" onChange={handleChange}/>
                    </Col>
                </Row>
                <Row className="pl-2 pr-2 mb-2 d-flex align-items-center">
                    <Col xs="12" className="mb-1">
                        <Label className="m-0">Oplaadkost</Label>
                    </Col>
                    <Col xs="12">
                        <input id="chargeCost" value={chargeCost.toFixed(2)} type="text" className="inputs-fixed-height form-control" readOnly/>
                    </Col>
                </Row>
                <Row className="pl-2 pr-2 mb-2 d-flex align-items-center">
                    <Col xs="12">
                        <Button color="primary">Registreer</Button>
                    </Col>
                </Row>
            </Form>
        </div>
        )
    }


return(
    <>
        {!props.isLoading && getChargeRegistrationForm()} 
    </>

)
}
export default UserChargesRegistrationForm;


